import { CheckBoxWidget, TextFieldWidget } from '@tableau/widgets';
import { CSSProperties } from 'react';
import React = require('react');
import { Messages } from '~src/codegen/Localize';
import { interpolateString } from '~src/common/utilities/StringInterpolation';
import { cssClass } from '~src/style';
import { L10n } from '~src/ts/L10n/L10n';

import { IResourceGovernanceSettingsKey } from '../types/ResourceGovernance';
const cssClassPrefix = 'resource-governance-limits-row';
const topLeftPadClass = cssClass(cssClassPrefix, 'left-pad', {
  paddingLeft: 7,
  paddingTop: 7,
  verticalAlign: 'middle',
});
const noLimitStyle: CSSProperties = {
  display: 'grid',
  minWidth: 60,
  justifyItems: 'center',
  alignItems: 'center',
  paddingTop: 15
};
const defaultValueStyle = cssClass(cssClassPrefix, 'default', {
  fontSize: 14,
  textAlign: 'center',
  verticalAlign: 'middle',
});

interface LimitsRowState {
  isChecked: boolean;
}

export interface ConcurrentJobLimitRowProps {
  limitType: string;
  value: number;
  originalValue: number;
  description: string;
  update: (value: string) => void;
  testId: IResourceGovernanceSettingsKey;
  defaultValue?: number;
  hasDefault?: boolean;
}

export function rowShouldAlert(value: number | undefined, original: number | undefined): boolean {
  // undefined cases handled automatically by "<";
  // undefined < [anything] === false, [anything] < undefined === false
  return value < original;
}
export class ResourceGovernanceLimitsRow extends React.Component<ConcurrentJobLimitRowProps, LimitsRowState> {
  public constructor(props: ConcurrentJobLimitRowProps) {
    super(props);
    this.state = {
      isChecked: props.value === undefined
    };
  }

  public render() {
    const description = interpolateString(this.props.description, {
      LIMIT_TYPE: () => <b key={`bold-text-${this.props.limitType}`}>{this.props.limitType}</b>
    });

    const alertStyle = {
      // http://design/guidelines/style/color Warning Global
      borderColor: '#feca63',
    };

    const handleCheckboxChange = (checked: boolean) => {
      this.setState({ isChecked: checked });
      if (checked) {
        this.props.update(undefined);
      } else {
        const valueToUpdateWith = this.props.originalValue !== undefined
          ? `${this.props.originalValue}`
          : '';
        this.props.update(valueToUpdateWith);
      }
    };

    const inputStyle = rowShouldAlert(this.props.value, this.props.originalValue) ? alertStyle : {};
    const onlyPositiveNums = (value: string) => !value || /^\d+\.?\d*$/.test(value);
    const inputValid = this.props.value !== undefined && !isNaN(this.props.value) && isFinite(this.props.value);
    const inputValue = inputValid ? this.props.value : '';
    const defaultValue = this.props.hasDefault
      ? this.props.defaultValue !== undefined
        ? L10n.localizedCount(this.props.defaultValue)
        : Messages.resourceGovernanceNoLimitValue()
      : undefined;

    // Disable inputting a value larger than what the backend can accept
    // if the frontend check is circumvented the user will get an error
    const maxJavaInt = 2147483647;
    const maxValidValue = Math.floor(maxJavaInt/3600);
    const boundValue = (value: string) => !value
      ? value // Let undefined/''/etc... through
      : String(Math.min(maxValidValue, Number(value)));
    return <tr key={`row-${this.props.limitType}`}>
      <td className={topLeftPadClass}>{description}</td>
      <td style={noLimitStyle}>
        <CheckBoxWidget
          testId={`no-limit-checkbox-${this.props.testId}`}
          checked={this.state.isChecked}
          handleChange={handleCheckboxChange}
        />
      </td>
      <td className={topLeftPadClass}>
        <TextFieldWidget
          label={''}
          testId={`input-${this.props.testId}`}
          value={inputValue}
          placeholder={this.state.isChecked ? Messages.resourceGovernanceNoLimitValue() : ''}
          disabled={this.state.isChecked}
          onChange={event => onlyPositiveNums(event.target.value) && this.props.update(boundValue(event.target.value))}
          style={inputStyle}
        />
      </td>
      {defaultValue && <td className={defaultValueStyle}><b>{defaultValue}</b></td>}
    </tr>;
  }
}